import { useEffect, useRef, useCallback } from "react";

export function useClickOutside<T extends HTMLElement>(
  callback: (...args: any[]) => void,
  excludeSelectors: string[] = [],
  excludeRefs: React.RefObject<HTMLElement>[] = [],
) {
  const ref = useRef<T>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Prevent closing when clicking on React Flow elements
      if (
        target?.closest(".react-flow__renderer") ||
        target?.closest(".react-flow__minimap") ||
        target?.closest(".react-flow__controls")
      ) {
        return;
      }

      // Check if click was on excluded elements by ref
      const isExcludedByRef = excludeRefs.some(
        (exRef) => exRef.current && exRef.current.contains(target),
      );

      if (isExcludedByRef) {
        return;
      }

      // Check if click was on excluded elements
      const isExcluded = excludeSelectors.some(
        (selector) => target.matches(selector) || target.closest(selector),
      );

      // Only close if click is outside and not on excluded elements
      if (
        ref.current &&
        !ref.current.contains(target) &&
        !isExcluded &&
        !isExcludedByRef
      ) {
        event.preventDefault();
        callback();
      }
    },
    [callback, excludeSelectors, excludeRefs],
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [handleClickOutside]);

  return ref;
}
